/**
 * Shared header for patient invoice and doctor bill HTML previews.
 * SwissAligner uses inline SVG (textColor) so html2canvas PDFs match preview — external SVG-in-<img> is flaky in capture.
 */

import { useState } from "react";
import { CONFIG, getBrandConfig, getBrandHex } from "@/config/constants.js";
import SwissAlignerLogo from "@/components/shared/SwissAlignerLogo/SwissAlignerLogo.jsx";
import {
  SWISSALIGNER_INVOICE_LOGO_HEIGHT_PX,
  SWISSALIGNER_INVOICE_LOGO_MAX_WIDTH_PX,
} from "./config/invoiceSwissAlignerLogo.js";
import { getActiveUiLocale } from "@/utils/invoices/documentTitles.js";
import { formatIdLine } from "@/utils/invoices/invoicePdfLabels.js";

/**
 * @param {object} props
 * @param {string} props.brand
 * @param {string} props.title
 * @param {string} [props.idSuffix]
 */
export default function InvoiceDocumentHeader({ brand, title, idSuffix = "" }) {
  const [logoError, setLogoError] = useState(false);
  const brandConfig = getBrandConfig(brand);
  const config = CONFIG.INVOICE;
  const brandColorHex = getBrandHex(brand);
  const idLabel = formatIdLine(idSuffix, getActiveUiLocale());

  return (
    <div className="invoice-header">
      <div className="invoice-header-top">
        <div className="invoice-logo">
          {brand === "SwissAligner" ? (
            <SwissAlignerLogo
              height={SWISSALIGNER_INVOICE_LOGO_HEIGHT_PX}
              className="logo-image logo-image--swissaligner"
              textColor={brandColorHex}
              svgPreserveAspectRatio="xMinYMid meet"
              style={{
                maxWidth: SWISSALIGNER_INVOICE_LOGO_MAX_WIDTH_PX,
              }}
            />
          ) : logoError ? (
            <span
              className="invoice-logo-fallback"
              style={{ color: brandColorHex, fontWeight: 700, fontSize: 18 }}
            >
              {config.COMPANY.NAME}
            </span>
          ) : (
            <img
              src={brandConfig.LOGO}
              alt={config.COMPANY.NAME}
              className="logo-image"
              onError={() => setLogoError(true)}
            />
          )}
        </div>
        <div className="invoice-id">{idLabel}</div>
      </div>
      <div className="invoice-title">{title}</div>
    </div>
  );
}
